/**
 * FilterBar — konfigurierbare horizontale Filter-Leiste fuer ProductView.
 *
 * Props:
 *   availableFilters  [{ id, label, kind, group? }]   — alle abgeleiteten Filter-Felder
 *   visibleFilterIds  string[]                         — aktuell sichtbare Felder (user-konfiguriert)
 *   onChangeVisible   (ids: string[]) => void          — Callback bei Aenderung der Sichtbarkeit
 *   facets            { categoryFacet, attributeFacets, categoryLabelMap }
 *   selectedMaterialTypes   string[]
 *   setSelectedMaterialTypes  (v) => void
 *   selectedStatuses        string[]
 *   setSelectedStatuses     (v) => void
 *   selectedCategories      string[]
 *   setSelectedCategories   (v) => void
 *   selectedAttrs           { [charName]: string[] }
 *   setSelectedAttrs        (v) => void | ((prev) => obj) => void
 *   filteredCount           number   — Anzahl gefilterter Produkte
 *   totalCount              number   — Gesamtanzahl
 *   hasAnyFilter            boolean
 *   onResetAll              () => void
 *   isDarkMode              boolean
 *   t                       (key, params?) => string
 *   MATERIAL_TYPE_OPTIONS   string[]
 *   PRODUCT_STATUS_OPTIONS  string[]
 */

import React from 'react';
import { SlidersHorizontal, ChevronDown, X, Check, Plus } from 'lucide-react';

const PREF_KEYS_STORAGE = 'product_filters';

// ---------------------------------------------------------------------------
// Hilfs-Hook: Popover mit Click-Outside-Schliessen
// ---------------------------------------------------------------------------
function usePopover() {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return { open, setOpen, ref };
}

// ---------------------------------------------------------------------------
// Einzelner Filter-Button mit Popover
// ---------------------------------------------------------------------------
function FilterPopover({
  id, label, kind, group,
  facets,
  selectedMaterialTypes, setSelectedMaterialTypes,
  selectedStatuses, setSelectedStatuses,
  selectedCategories, setSelectedCategories,
  selectedManufacturers, setSelectedManufacturers,
  selectedAttrs, setSelectedAttrs,
  isDarkMode, t,
  MATERIAL_TYPE_OPTIONS, PRODUCT_STATUS_OPTIONS,
}) {
  const { open, setOpen, ref } = usePopover();

  const { categoryFacet, attributeFacets, categoryLabelMap, manufacturerFacet } = facets;

  // aktive Werte fuer diesen Filter
  let activeValues = [];
  if (id === 'material_type') activeValues = selectedMaterialTypes;
  else if (id === 'status') activeValues = selectedStatuses;
  else if (id === 'category') activeValues = selectedCategories;
  else if (id === 'manufacturer') activeValues = selectedManufacturers;
  else if (id.startsWith('attr:')) {
    const charName = id.slice(5);
    activeValues = selectedAttrs[charName] || [];
  }

  const activeCount = activeValues.length;

  // Werte-Liste fuer diesen Filter
  let options = [];
  if (id === 'material_type') {
    options = MATERIAL_TYPE_OPTIONS.map(v => ({ value: v, label: v, count: null }));
  } else if (id === 'status') {
    options = PRODUCT_STATUS_OPTIONS.map(v => ({
      value: v,
      label: t(`products.status.${v}`),
      count: null,
    }));
  } else if (id === 'category') {
    options = categoryFacet.map(([cat, cnt]) => ({
      value: cat,
      label: categoryLabelMap[cat] || cat,
      count: cnt,
    }));
  } else if (id === 'manufacturer') {
    options = manufacturerFacet.map(([m, cnt]) => ({ value: m, label: m, count: cnt }));
  } else if (id.startsWith('attr:')) {
    const charName = id.slice(5);
    const attrGroup = attributeFacets.find(g =>
      g.chars.some(c => c.charName === charName)
    );
    if (attrGroup) {
      const char = attrGroup.chars.find(c => c.charName === charName);
      if (char) {
        options = char.values.map(([val, cnt]) => ({
          value: val,
          label: val,
          count: cnt,
        }));
      }
    }
  }

  const toggle = (value) => {
    if (id === 'material_type') {
      setSelectedMaterialTypes(prev =>
        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
      );
    } else if (id === 'status') {
      setSelectedStatuses(prev =>
        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
      );
    } else if (id === 'category') {
      setSelectedCategories(prev =>
        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
      );
    } else if (id === 'manufacturer') {
      setSelectedManufacturers(prev =>
        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
      );
    } else if (id.startsWith('attr:')) {
      const charName = id.slice(5);
      setSelectedAttrs(prev => {
        const current = prev[charName] || [];
        const next = current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value];
        if (next.length === 0) {
          const { [charName]: _removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [charName]: next };
      });
    }
  };

  const clearFilter = (e) => {
    e.stopPropagation();
    if (id === 'material_type') setSelectedMaterialTypes([]);
    else if (id === 'status') setSelectedStatuses([]);
    else if (id === 'category') setSelectedCategories([]);
    else if (id === 'manufacturer') setSelectedManufacturers([]);
    else if (id.startsWith('attr:')) {
      const charName = id.slice(5);
      setSelectedAttrs(prev => {
        const { [charName]: _r, ...rest } = prev;
        return rest;
      });
    }
  };

  const btnBase = `
    relative flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium
    transition-all duration-150 select-none cursor-pointer whitespace-nowrap
  `;
  const btnActive = isDarkMode
    ? 'bg-brand/20 border-brand/50 text-brand'
    : 'bg-brand/10 border-brand/30 text-brand';
  const btnInactive = isDarkMode
    ? 'bg-gray-800/40 border-white/15 text-white/70 hover:border-white/30 hover:text-white'
    : 'bg-white border-slate-200 text-slate-600 hover:border-brand/30 hover:text-brand';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className={`${btnBase} ${activeCount > 0 ? btnActive : btnInactive}`}
      >
        <span>{label}</span>
        {activeCount > 0 && (
          <>
            <span className={`
              inline-flex items-center justify-center min-w-[18px] h-[18px] px-1
              rounded-full text-[10px] font-bold bg-brand text-white
            `}>
              {activeCount}
            </span>
            <button
              onClick={clearFilter}
              className={`ml-0.5 rounded-full p-0.5 transition-colors ${
                isDarkMode ? 'hover:bg-white/15' : 'hover:bg-brand/20'
              }`}
              title={t('products.filterReset')}
            >
              <X className="w-3 h-3" />
            </button>
          </>
        )}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && options.length > 0 && (
        <div className={`
          absolute top-full left-0 mt-1.5 z-50 min-w-[200px] max-w-[260px]
          rounded-xl border shadow-xl overflow-hidden
          ${isDarkMode
            ? 'bg-gray-900 border-white/15 shadow-black/50'
            : 'bg-white border-slate-200 shadow-slate-200/80'}
        `}>
          <div className="max-h-64 overflow-y-auto py-1.5 px-1.5 flex flex-col gap-0.5">
            {options.map(opt => {
              const active = activeValues.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  className={`
                    flex items-center gap-2 w-full text-left px-2.5 py-1.5 rounded-lg
                    text-xs font-medium transition-all duration-100
                    ${active
                      ? isDarkMode
                        ? 'bg-brand/25 text-brand'
                        : 'bg-brand/10 text-brand'
                      : isDarkMode
                        ? 'text-white/70 hover:text-white hover:bg-white/5'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}
                  `}
                >
                  <span className={`
                    w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center
                    transition-all
                    ${active
                      ? 'bg-brand border-brand'
                      : isDarkMode ? 'border-white/30' : 'border-slate-300'}
                  `}>
                    {active && <Check className="w-2.5 h-2.5 text-white" />}
                  </span>
                  <span className="flex-1 truncate">{opt.label}</span>
                  {opt.count != null && (
                    <span className={`text-[10px] flex-shrink-0 ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>
                      {opt.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Feldauswahl-Picker Popover (+ Filter Button)
// ---------------------------------------------------------------------------
function FilterFieldPicker({
  availableFilters,
  visibleFilterIds,
  onChangeVisible,
  isDarkMode,
  t,
}) {
  const { open, setOpen, ref } = usePopover();

  // Gruppen fuer die Anzeige
  const masterdataIds = new Set(['material_type', 'status', 'category', 'manufacturer']);
  const masterdataFilters = availableFilters.filter(f => masterdataIds.has(f.id));

  // Klassifizierungs-Merkmale: nach char_group gruppieren
  const attrFilters = availableFilters.filter(f => f.id.startsWith('attr:'));
  const attrGroups = {};
  attrFilters.forEach(f => {
    const g = f.group || t('products.filterAttributes');
    if (!attrGroups[g]) attrGroups[g] = [];
    attrGroups[g].push(f);
  });

  const toggleId = (id) => {
    const next = visibleFilterIds.includes(id)
      ? visibleFilterIds.filter(v => v !== id)
      : [...visibleFilterIds, id];
    onChangeVisible(next);
  };

  const btnCls = isDarkMode
    ? 'flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-dashed border-white/25 text-white/60 hover:border-brand/50 hover:text-brand text-xs font-medium transition-all duration-150 cursor-pointer'
    : 'flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-dashed border-slate-300 text-slate-500 hover:border-brand/50 hover:text-brand text-xs font-medium transition-all duration-150 cursor-pointer';

  const renderGroup = (label, filters) => {
    if (filters.length === 0) return null;
    return (
      <div key={label} className="mb-2">
        <p className={`text-[10px] font-semibold uppercase tracking-wide px-2.5 pb-1 pt-0.5 ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>
          {label}
        </p>
        {filters.map(f => {
          const checked = visibleFilterIds.includes(f.id);
          return (
            <button
              key={f.id}
              onClick={() => toggleId(f.id)}
              className={`
                flex items-center gap-2 w-full text-left px-2.5 py-1.5 rounded-lg
                text-xs font-medium transition-all duration-100
                ${checked
                  ? isDarkMode ? 'bg-brand/20 text-brand' : 'bg-brand/10 text-brand'
                  : isDarkMode ? 'text-white/70 hover:text-white hover:bg-white/5' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}
              `}
            >
              <span className={`
                w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center transition-all
                ${checked ? 'bg-brand border-brand' : isDarkMode ? 'border-white/30' : 'border-slate-300'}
              `}>
                {checked && <Check className="w-2.5 h-2.5 text-white" />}
              </span>
              <span className="flex-1 truncate">{f.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(v => !v)} className={btnCls}>
        <Plus className="w-3.5 h-3.5" />
        {t('products.addFilter')}
      </button>

      {open && (
        <div className={`
          absolute top-full right-0 mt-1.5 z-50 w-64
          rounded-xl border shadow-xl overflow-hidden
          ${isDarkMode
            ? 'bg-gray-900 border-white/15 shadow-black/50'
            : 'bg-white border-slate-200 shadow-slate-200/80'}
        `}>
          <div className="px-3 pt-3 pb-1">
            <p className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
              {t('products.filterFields')}
            </p>
          </div>
          <div className="max-h-72 overflow-y-auto px-1.5 py-1.5">
            {renderGroup(t('products.filterGroupMasterdata'), masterdataFilters)}
            {Object.entries(attrGroups).map(([grp, filters]) =>
              renderGroup(grp, filters)
            )}
            {availableFilters.length === 0 && (
              <p className={`text-xs px-2.5 py-2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                {t('products.empty')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FilterBar — Hauptkomponente
// ---------------------------------------------------------------------------
export default function FilterBar({
  availableFilters,
  visibleFilterIds,
  onChangeVisible,
  facets,
  selectedMaterialTypes, setSelectedMaterialTypes,
  selectedStatuses, setSelectedStatuses,
  selectedCategories, setSelectedCategories,
  selectedManufacturers, setSelectedManufacturers,
  selectedAttrs, setSelectedAttrs,
  filteredCount,
  totalCount,
  hasAnyFilter,
  onResetAll,
  isDarkMode,
  t,
  MATERIAL_TYPE_OPTIONS,
  PRODUCT_STATUS_OPTIONS,
}) {
  const visibleFilters = availableFilters.filter(f => visibleFilterIds.includes(f.id));

  const barCls = isDarkMode
    ? 'flex flex-wrap items-center gap-2 px-4 py-3 rounded-2xl bg-gray-900/40 border border-white/15 backdrop-blur-xl mb-5'
    : 'flex flex-wrap items-center gap-2 px-4 py-3 rounded-2xl bg-white border border-slate-200 shadow-sm mb-5';

  const subCls = isDarkMode ? 'text-white/50' : 'text-slate-400';

  return (
    <div className={barCls}>
      {/* Filter-Icon */}
      <SlidersHorizontal className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`} />

      {/* Aktive Filter-Buttons */}
      {visibleFilters.map(f => (
        <FilterPopover
          key={f.id}
          id={f.id}
          label={f.label}
          kind={f.kind}
          group={f.group}
          facets={facets}
          selectedMaterialTypes={selectedMaterialTypes}
          setSelectedMaterialTypes={setSelectedMaterialTypes}
          selectedStatuses={selectedStatuses}
          setSelectedStatuses={setSelectedStatuses}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          selectedManufacturers={selectedManufacturers}
          setSelectedManufacturers={setSelectedManufacturers}
          selectedAttrs={selectedAttrs}
          setSelectedAttrs={setSelectedAttrs}
          isDarkMode={isDarkMode}
          t={t}
          MATERIAL_TYPE_OPTIONS={MATERIAL_TYPE_OPTIONS}
          PRODUCT_STATUS_OPTIONS={PRODUCT_STATUS_OPTIONS}
        />
      ))}

      {/* + Filter Picker */}
      <FilterFieldPicker
        availableFilters={availableFilters}
        visibleFilterIds={visibleFilterIds}
        onChangeVisible={onChangeVisible}
        isDarkMode={isDarkMode}
        t={t}
      />

      {/* Spacer + Counter + Reset */}
      <div className="ml-auto flex items-center gap-3 flex-shrink-0">
        <span className={`text-xs ${subCls}`}>
          <span className="font-semibold text-brand">{filteredCount}</span>
          {' '}{t('products.filterOf')}{' '}
          <span className="font-semibold">{totalCount}</span>
        </span>
        {hasAnyFilter && (
          <button
            onClick={onResetAll}
            className={`text-xs font-medium px-2.5 py-1 rounded-lg transition-colors ${
              isDarkMode
                ? 'text-white/60 hover:text-white hover:bg-white/10'
                : 'text-slate-500 hover:text-brand hover:bg-brand/5'
            }`}
          >
            {t('products.filterReset')}
          </button>
        )}
      </div>
    </div>
  );
}
